<template>
  <public-layout>
    <div class="search-page">
      <div class="sc-container">

        <!-- Header -->
        <div class="search-header">
          <h1 class="search-header__title" v-if="$route.query.q">
            Results for <em>"{{ $route.query.q }}"</em>
          </h1>
          <h1 class="search-header__title" v-else>Search Results</h1>
          <!-- Active filter chips -->
          <div v-if="$route.query.country_id || $route.query.state_id || $route.query.city_id || $route.query.type" class="search-filters">
            <span class="search-filter-label">Filters:</span>
            <span v-if="$route.query.type" class="search-filter-chip">
              Type: {{ { project:'Projects', property:'Properties', plan:'Concepts', user:'People', company:'Companies' }[$route.query.type] || $route.query.type }}
            </span>
            <span v-if="$route.query.country_id" class="search-filter-chip">Country ID: {{ $route.query.country_id }}</span>
            <span v-if="$route.query.state_id" class="search-filter-chip">State ID: {{ $route.query.state_id }}</span>
            <span v-if="$route.query.city_id" class="search-filter-chip">City ID: {{ $route.query.city_id }}</span>
            <a :href="$route.query.q ? '/search?q=' + encodeURIComponent($route.query.q) : '/search'" class="search-filter-clear">Clear filters</a>
          </div>
          <p class="search-header__sub" v-if="!loading">
            <span v-if="activeTab === 'all'">{{ projects.length + users.length + business.length + properties.length + plans.length + blogs.length }} result{{ (projects.length + users.length + business.length + properties.length + plans.length + blogs.length) !== 1 ? 's' : '' }} found</span>
            <span v-else-if="activeTab === 'projects'">{{ projects.length }} project{{ projects.length !== 1 ? 's' : '' }} found</span>
            <span v-else-if="activeTab === 'professionals'">{{ users.length }} professional{{ users.length !== 1 ? 's' : '' }} found</span>
            <span v-else-if="activeTab === 'companies'">{{ business.length }} {{ business.length !== 1 ? 'companies' : 'company' }} found</span>
            <span v-else-if="activeTab === 'properties'">{{ properties.length }} propert{{ properties.length !== 1 ? 'ies' : 'y' }} found</span>
            <span v-else-if="activeTab === 'plans'">{{ plans.length }} concept{{ plans.length !== 1 ? 's' : '' }} found</span>
            <span v-else>{{ blogs.length }} article{{ blogs.length !== 1 ? 's' : '' }} found</span>
          </p>
        </div>

        <!-- Filter tabs -->
        <div class="filter-tabs" role="tablist">
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'all' }"
            @click="activeTab = 'all'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            All
            <span class="filter-tab__count">{{ projects.length + users.length + business.length + properties.length + plans.length + blogs.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'projects' }"
            @click="activeTab = 'projects'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Projects
            <span class="filter-tab__count">{{ projects.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'professionals' }"
            @click="activeTab = 'professionals'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Professionals
            <span class="filter-tab__count">{{ users.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'companies' }"
            @click="activeTab = 'companies'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            Companies
            <span class="filter-tab__count">{{ business.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'properties' }"
            @click="activeTab = 'properties'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Properties
            <span class="filter-tab__count">{{ properties.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'plans' }"
            @click="activeTab = 'plans'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Concepts
            <span class="filter-tab__count">{{ plans.length }}</span>
          </button>
          <button
            class="filter-tab"
            :class="{ 'filter-tab--active': activeTab === 'news' }"
            @click="activeTab = 'news'"
            role="tab"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8M15 18h-5M10 6h8v4h-8z"/></svg>
            News
            <span class="filter-tab__count">{{ blogs.length }}</span>
          </button>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="results-grid">
          <div v-for="n in 6" :key="n" class="skeleton-card">
            <div class="skeleton skeleton--img"></div>
            <div class="skeleton-card__body">
              <div class="skeleton skeleton--title"></div>
              <div class="skeleton skeleton--text"></div>
              <div class="skeleton skeleton--text skeleton--text-short"></div>
            </div>
          </div>
        </div>

        <!-- All Tab -->
        <div v-else-if="activeTab === 'all'" role="tabpanel">
          <div v-if="projects.length + users.length + business.length + properties.length + plans.length + blogs.length === 0" class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <h3>No results found</h3>
            <p>Try different keywords or browse listings directly.</p>
            <router-link to="/listings" class="btn-primary">Browse Listings</router-link>
          </div>
          <template v-else>

            <!-- Projects section -->
            <div v-if="projects.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  Projects
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'projects'">See all {{ projects.length }} →</button>
              </div>
              <div class="results-grid results-grid--projects">
                <div v-for="(project, index) in projects.slice(0, 3)" :key="project.id || index" class="result-card">
                  <router-link :to="'/listing/project/' + project.slug" class="result-card__img-link">
                    <img v-if="project.images && project.images.length" class="result-card__img"
                      :src="project.images[0].path || (imgUrl + 'projects/' + project.images[0].image)" :alt="project.name" />
                    <div v-else class="result-card__img-placeholder"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
                  </router-link>
                  <div class="result-card__body">
                    <div class="result-card__meta-row">
                      <span class="result-card__badge result-card__badge--project">Project</span>
                    </div>
                    <router-link :to="'/listing/project/' + project.slug" class="result-card__title">{{ project.name }}</router-link>
                    <p class="result-card__desc" v-if="project.description">{{ stripHtml(project.description).substring(0, 100) }}…</p>
                    <router-link :to="'/listing/project/' + project.slug" class="result-card__cta">View Project <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Properties section -->
            <div v-if="properties.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  Properties
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'properties'">See all {{ properties.length }} →</button>
              </div>
              <div class="results-grid results-grid--projects">
                <div v-for="prop in properties.slice(0, 3)" :key="prop.id" class="result-card">
                  <router-link :to="'/listing/property/' + prop.slug" class="result-card__img-link">
                    <img v-if="prop.images && prop.images.length" class="result-card__img"
                      :src="prop.images[0].path || (imgUrl + 'posts/' + prop.images[0].image)" :alt="prop.name" />
                    <div v-else class="result-card__img-placeholder"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
                  </router-link>
                  <div class="result-card__body">
                    <div class="result-card__meta-row"><span class="result-card__badge result-card__badge--property">Property</span></div>
                    <router-link :to="'/listing/property/' + prop.slug" class="result-card__title">{{ prop.name }}</router-link>
                    <p class="result-card__desc" v-if="prop.description">{{ stripHtml(prop.description).substring(0, 100) }}…</p>
                    <router-link :to="'/listing/property/' + prop.slug" class="result-card__cta">View Property <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plans section -->
            <div v-if="plans.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Concepts
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'plans'">See all {{ plans.length }} →</button>
              </div>
              <div class="results-grid results-grid--projects">
                <div v-for="plan in plans.slice(0, 3)" :key="plan.id" class="result-card">
                  <router-link :to="'/listing/plan/' + plan.slug" class="result-card__img-link">
                    <img v-if="plan.images && plan.images.length" class="result-card__img"
                      :src="plan.images[0].path || (imgUrl + 'plans/' + plan.images[0].image)" :alt="plan.name" />
                    <div v-else class="result-card__img-placeholder"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>
                  </router-link>
                  <div class="result-card__body">
                    <div class="result-card__meta-row"><span class="result-card__badge result-card__badge--plan">Concept</span></div>
                    <router-link :to="'/listing/plan/' + plan.slug" class="result-card__title">{{ plan.name }}</router-link>
                    <p class="result-card__desc" v-if="plan.description">{{ stripHtml(plan.description).substring(0, 100) }}…</p>
                    <router-link :to="'/listing/plan/' + plan.slug" class="result-card__cta">View Concept <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Professionals section -->
            <div v-if="users.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Professionals
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'professionals'">See all {{ users.length }} →</button>
              </div>
              <div class="results-list--people">
                <div v-for="u in users.slice(0, 2)" :key="u.id" class="person-card">
                  <div class="person-card__header">
                    <router-link :to="'/sc/profile/' + u.slug" class="person-card__avatar-link">
                      <img v-if="u.avatar" class="person-card__avatar" :src="$avatarSrc(u.avatar, 'users')" :alt="u.name" />
                      <img v-else class="person-card__avatar" :src="initAvatar(u.name, 60)" :alt="u.name" />
                    </router-link>
                    <router-link :to="'/sc/profile/' + u.slug" class="person-card__info">
                      <strong class="person-card__name">{{ u.name }}</strong>
                      <span v-if="u.profession" class="person-card__role">{{ u.profession }}</span>
                      <span v-else class="person-card__role">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {{ u.city ? u.city.name + ', ' : '' }}{{ u.country ? u.country.name : '' }}
                      </span>
                    </router-link>
                    <div class="person-card__actions">
                      <router-link :to="'/sc/profile/' + u.slug" class="person-card__view">View Profile</router-link>
                      <button class="person-card__follow" @click="follow(u.id)">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Follow
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Companies section -->
            <div v-if="business.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                  Companies
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'companies'">See all {{ business.length }} →</button>
              </div>
              <div class="results-grid results-grid--companies">
                <div v-for="biz in business.slice(0, 3)" :key="biz.id" class="company-card">
                  <router-link :to="'/company/' + biz.slug" class="company-card__logo-link">
                    <img v-if="biz.logo" class="company-card__logo" :src="imgUrl + 'users/' + biz.logo" :alt="biz.name" />
                    <img v-else class="company-card__logo" :src="initAvatar(biz.name, 52)" :alt="biz.name" />
                  </router-link>
                  <div class="company-card__info">
                    <router-link :to="'/company/' + biz.slug" class="company-card__name">{{ biz.name }}</router-link>
                    <span class="company-card__loc" v-if="biz.country">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {{ biz.state ? biz.state.name + ', ' : '' }}{{ biz.country.name }}
                    </span>
                  </div>
                  <router-link :to="'/company/' + biz.slug" class="company-card__cta">View</router-link>
                </div>
              </div>
            </div>

            <!-- News section -->
            <div v-if="blogs.length" class="all-section">
              <div class="all-section__header">
                <h3 class="all-section__title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/></svg>
                  News
                </h3>
                <button class="all-section__see-all" @click="activeTab = 'news'">See all {{ blogs.length }} →</button>
              </div>
              <div class="results-grid results-grid--news">
                <a v-for="post in blogs.slice(0, 3)" :key="post.id" :href="'/blog/' + post.slug" class="news-card">
                  <div class="news-card__img-wrap">
                    <img v-if="post.images && post.images.length" class="news-card__img"
                      :src="post.images[0].path || (imgUrl + 'blog/' + post.images[0].image)" :alt="post.title" />
                    <div v-else class="news-card__img-placeholder"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/></svg></div>
                  </div>
                  <div class="news-card__body">
                    <span v-if="post.category" class="news-card__cat">{{ post.category.name }}</span>
                    <h4 class="news-card__title">{{ post.title }}</h4>
                    <span class="news-card__cta">Read Article <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></span>
                  </div>
                </a>
              </div>
            </div>

          </template>
        </div>

        <!-- Projects Tab -->
        <div v-else-if="activeTab === 'projects'" role="tabpanel">
          <div v-if="projects.length" class="results-grid results-grid--projects">
            <div v-for="(project, index) in projects" :key="project.id || index" class="result-card">
              <router-link :to="'/listing/project/' + project.slug" class="result-card__img-link">
                <img
                  v-if="project.images && project.images.length"
                  class="result-card__img"
                  :src="project.images[0].path || (imgUrl + 'projects/' + project.images[0].image)"
                  :alt="project.name"
                />
                <div v-else class="result-card__img-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </div>
              </router-link>

              <div class="result-card__body">
                <div class="result-card__meta-row">
                  <span class="result-card__badge result-card__badge--project">Project</span>
                  <span class="result-card__status" :class="project.status == 1 ? 'result-card__status--done' : 'result-card__status--ongoing'">
                    {{ project.status == 1 ? 'Completed' : 'On-going' }}
                  </span>
                </div>

                <router-link :to="'/listing/project/' + project.slug" class="result-card__title">{{ project.name }}</router-link>

                <p class="result-card__location" v-if="project.country">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ project.state ? project.state.name + ', ' : '' }}{{ project.country.name }}
                </p>

                <p class="result-card__desc" v-if="project.description">
                  {{ stripHtml(project.description).substring(0, 120) + '…' }}
                </p>

                <!-- Team tags -->
                <div v-if="project.tags && project.tags.length" class="result-card__team" @click="viewTeam(index)">
                  <div class="team-avatars">
                    <img
                      v-for="tag in project.tags.slice(0, 5)"
                      :key="tag.id"
                      :src="tag.user && tag.user.avatar ? $avatarSrc(tag.user.avatar) : initAvatar(tag.user ? tag.user.name : '')"
                      :alt="tag.user ? tag.user.name : ''"
                      class="team-avatar"
                    />
                  </div>
                  <span class="team-count">{{ project.tags.length }} connection{{ project.tags.length !== 1 ? 's' : '' }}</span>
                </div>

                <router-link :to="'/listing/project/' + project.slug" class="result-card__cta">
                  View Project
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Empty: projects -->
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <h3>No projects found</h3>
            <p>Try different keywords or browse all projects.</p>
            <router-link to="/listings?type=project" class="btn-primary">Browse Projects</router-link>
          </div>
        </div>

        <!-- Professionals Tab -->
        <div v-else-if="activeTab === 'professionals'" role="tabpanel">
          <div v-if="users.length" class="results-list--people">
            <div v-for="u in users" :key="u.id" class="person-card">
              <!-- Profile header row -->
              <div class="person-card__header">
                <router-link :to="'/sc/profile/' + u.slug" class="person-card__avatar-link">
                  <img v-if="u.avatar" class="person-card__avatar" :src="$avatarSrc(u.avatar, 'users')" :alt="u.name" />
                  <img v-else class="person-card__avatar" src="/assets/avatar.png" :alt="u.name" />
                </router-link>

                <router-link :to="'/sc/profile/' + u.slug" class="person-card__info">
                  <strong class="person-card__name">{{ u.name }}</strong>
                  <span v-if="u.profession" class="person-card__role">{{ u.profession }}</span>
                  <span v-else class="person-card__role">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {{ u.city ? u.city.name + ', ' : '' }}{{ u.state ? u.state.name + ', ' : '' }}{{ u.country ? u.country.name : '' }}
                  </span>
                </router-link>

                <div class="person-card__actions">
                  <router-link :to="'/sc/profile/' + u.slug" class="person-card__view">View Profile</router-link>
                  <button class="person-card__follow" @click="follow(u.id)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Follow
                  </button>
                </div>
              </div>

              <!-- User content sections -->
              <div v-if="(u.projects && u.projects.length) || (u.posts && u.posts.length) || (u.plans && u.plans.length)" class="person-card__content">
                <!-- Projects -->
                <div v-if="u.projects && u.projects.length" class="person-card__section">
                  <h4 class="person-card__section-label">Projects</h4>
                  <div class="person-card__mini-row">
                    <router-link v-for="p in u.projects" :key="p.id" :to="'/listing/project/' + p.slug" class="person-card__mini-card">
                      <img v-if="p.images && p.images.length" class="person-card__mini-img"
                        :src="p.images[0].path || (imgUrl + 'projects/' + p.images[0].image)" :alt="p.name" />
                      <div v-else class="person-card__mini-placeholder">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="m9 9 3-3 3 3M12 6v9"/></svg>
                      </div>
                      <span class="person-card__mini-title">{{ p.name }}</span>
                    </router-link>
                  </div>
                </div>

                <!-- Properties -->
                <div v-if="u.posts && u.posts.length" class="person-card__section">
                  <h4 class="person-card__section-label">Properties</h4>
                  <div class="person-card__mini-row">
                    <router-link v-for="p in u.posts" :key="p.id" :to="'/listing/property/' + p.slug" class="person-card__mini-card">
                      <img v-if="p.images && p.images.length" class="person-card__mini-img"
                        :src="p.images[0].path || (imgUrl + 'posts/' + p.images[0].image)" :alt="p.name" />
                      <div v-else class="person-card__mini-placeholder">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      </div>
                      <span class="person-card__mini-title">{{ p.name }}</span>
                    </router-link>
                  </div>
                </div>

                <!-- Plans -->
                <div v-if="u.plans && u.plans.length" class="person-card__section">
                  <h4 class="person-card__section-label">Concepts</h4>
                  <div class="person-card__mini-row">
                    <router-link v-for="p in u.plans" :key="p.id" :to="'/listing/plan/' + p.slug" class="person-card__mini-card">
                      <img v-if="p.images && p.images.length" class="person-card__mini-img"
                        :src="p.images[0].path || (imgUrl + 'plans/' + p.images[0].image)" :alt="p.name" />
                      <div v-else class="person-card__mini-placeholder">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      </div>
                      <span class="person-card__mini-title">{{ p.name }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty: professionals -->
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <h3>No professionals found</h3>
            <p>Try broader keywords to find construction professionals.</p>
          </div>
        </div>

        <!-- Companies Tab -->
        <div v-else-if="activeTab === 'companies'" role="tabpanel">
          <div v-if="business.length" class="results-grid results-grid--companies">
            <div v-for="biz in business" :key="biz.id" class="company-card">
              <router-link :to="'/company/' + biz.slug" class="company-card__logo-link">
                <img
                  v-if="biz.logo"
                  class="company-card__logo"
                  :src="imgUrl + 'users/' + biz.logo"
                  :alt="biz.name"
                />
                <img v-else class="company-card__logo" :src="initAvatar(biz.name, 52)" :alt="biz.name" />
              </router-link>

              <div class="company-card__info">
                <router-link :to="'/company/' + biz.slug" class="company-card__name">{{ biz.name }}</router-link>
                <span class="company-card__loc" v-if="biz.country">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ biz.state ? biz.state.name + ', ' : '' }}{{ biz.country.name }}
                </span>
              </div>

              <router-link :to="'/company/' + biz.slug" class="company-card__cta">View</router-link>
            </div>
          </div>

          <!-- Empty: companies -->
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            <h3>No companies found</h3>
            <p>Try different keywords to find construction companies.</p>
          </div>
        </div>

        <!-- Properties Tab -->
        <div v-else-if="activeTab === 'properties'" role="tabpanel">
          <div v-if="properties.length" class="results-grid results-grid--projects">
            <div v-for="prop in properties" :key="prop.id" class="result-card">
              <router-link :to="'/listing/property/' + prop.slug" class="result-card__img-link">
                <img
                  v-if="prop.images && prop.images.length"
                  class="result-card__img"
                  :src="prop.images[0].path || (imgUrl + 'posts/' + prop.images[0].image)"
                  :alt="prop.name"
                />
                <div v-else class="result-card__img-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                </div>
              </router-link>
              <div class="result-card__body">
                <div class="result-card__meta-row">
                  <span class="result-card__badge result-card__badge--property">Property</span>
                </div>
                <router-link :to="'/listing/property/' + prop.slug" class="result-card__title">{{ prop.name }}</router-link>
                <p class="result-card__location" v-if="prop.country">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ prop.city ? prop.city.name + ', ' : '' }}{{ prop.country.name }}
                </p>
                <p class="result-card__desc" v-if="prop.description">{{ stripHtml(prop.description).substring(0, 120) + '…' }}</p>
                <router-link :to="'/listing/property/' + prop.slug" class="result-card__cta">
                  View Property
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <h3>No properties found</h3>
            <p>Try different keywords to find real estate listings.</p>
            <router-link to="/listings?type=property" class="btn-primary">Browse Properties</router-link>
          </div>
        </div>

        <!-- Plans Tab -->
        <div v-else-if="activeTab === 'plans'" role="tabpanel">
          <div v-if="plans.length" class="results-grid results-grid--projects">
            <div v-for="plan in plans" :key="plan.id" class="result-card">
              <router-link :to="'/listing/plan/' + plan.slug" class="result-card__img-link">
                <img
                  v-if="plan.images && plan.images.length"
                  class="result-card__img"
                  :src="plan.images[0].path || (imgUrl + 'plans/' + plan.images[0].image)"
                  :alt="plan.name"
                />
                <div v-else class="result-card__img-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
              </router-link>
              <div class="result-card__body">
                <div class="result-card__meta-row">
                  <span class="result-card__badge result-card__badge--plan">Concept</span>
                </div>
                <router-link :to="'/listing/plan/' + plan.slug" class="result-card__title">{{ plan.name }}</router-link>
                <p class="result-card__location" v-if="plan.user">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  {{ plan.user.name }}
                </p>
                <p class="result-card__desc" v-if="plan.description">{{ stripHtml(plan.description).substring(0, 120) + '...' }}</p>
                <router-link :to="'/listing/plan/' + plan.slug" class="result-card__cta">
                  View Concept
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <h3>No concepts found</h3>
            <p>Try different keywords to find architectural concepts.</p>
            <router-link to="/listings?type=plan" class="btn-primary">Browse Concepts</router-link>
          </div>
        </div>

        <!-- News/Blog Tab -->
        <div v-else-if="activeTab === 'news'" role="tabpanel">
          <div v-if="blogs.length" class="results-grid results-grid--news">
            <a
              v-for="post in blogs"
              :key="post.id"
              :href="'/blog/' + post.slug"
              class="news-card"
            >
              <div class="news-card__img-wrap">
                <img
                  v-if="post.images && post.images.length"
                  class="news-card__img"
                  :src="post.images[0].path || (imgUrl + 'blog/' + post.images[0].image)"
                  :alt="post.title"
                />
                <div v-else class="news-card__img-placeholder">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/></svg>
                </div>
              </div>
              <div class="news-card__body">
                <span v-if="post.category" class="news-card__cat">{{ post.category.name }}</span>
                <h4 class="news-card__title">{{ post.title }}</h4>
                <p class="news-card__desc">{{ stripHtml(post.description || '').substring(0, 110) }}…</p>
                <span class="news-card__cta">
                  Read Article
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </span>
              </div>
            </a>
          </div>
          <div v-else class="search-empty">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/></svg>
            <h3>No articles found</h3>
            <p>Try different keywords to find news and articles.</p>
            <router-link to="/blog" class="btn-primary">Browse Blog</router-link>
          </div>
        </div>

      </div><!-- /.sc-container -->

      <!-- Team connections modal -->
      <transition name="modal-fade">
        <div v-if="showTeamModal && currentData && currentData.tags" class="modal-overlay" @click.self="showTeamModal = false">
          <div class="modal-panel modal-panel--sm">
            <div class="modal-panel__header">
              <h2 class="modal-panel__title">{{ currentData.name }} — Team</h2>
              <button class="modal-panel__close" @click="showTeamModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="modal-panel__body">
              <div v-for="tag in currentData.tags" :key="tag.id" class="team-member-row">
                <a :href="'/sc/profile/' + tag.user.slug">
                  <img
                    v-if="tag.user.avatar"
                    class="team-member-row__avatar"
                    :src="$avatarSrc(tag.user.avatar)"
                    :alt="tag.user.name"
                  />
                  <img v-else class="team-member-row__avatar" :src="initAvatar(tag.user.name, 44)" :alt="tag.user.name" />
                </a>
                <div class="team-member-row__info">
                  <router-link :to="'/sc/profile/' + tag.user.slug" class="team-member-row__name">{{ tag.user.name }}</router-link>
                  <span class="team-member-row__role">{{ tag.user.profession }}</span>
                </div>
                <button class="person-card__follow" @click="follow(tag.user.id)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Follow
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

    </div><!-- /.search-page -->
  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';

export default {
  name: 'Search',
  components: { PublicLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: {},
      user: {},
      projects: [],
      users: [],
      business: [],
      plans: [],
      blogs: [],
      properties: [],
      currentData: {},
      loading: false,
      id: localStorage.getItem('user'),
      activeTab: 'all',
      showTeamModal: false,
    };
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title || 'Search Listing';
        this.fetchData();
      },
    },
  },

  created() {
    this.fetchCompany();
    this.fetchUser();
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.push({ name: 'Login' });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.user = res.data; })
        .catch(() => {});
    },

    fetchData() {
      const q         = this.$route.query.q          || '';
      const type      = this.$route.query.type        || '';
      const countryId = this.$route.query.country_id  || '';
      const stateId   = this.$route.query.state_id    || '';
      const cityId    = this.$route.query.city_id     || '';

      if (!q && !countryId && !stateId && !cityId) return;

      if (type) this.activeTab = type;

      this.loading = true;
      const params = new URLSearchParams();
      if (q)         params.set('q',          q);
      if (type)      params.set('type',       type);
      if (countryId) params.set('country_id', countryId);
      if (stateId)   params.set('state_id',   stateId);
      if (cityId)    params.set('city_id',    cityId);

      axios(process.env.VUE_APP_API_URL + 'search?' + params.toString())
        .then(res => {
          if (res.status === 200) {
            this.projects   = res.data.project   || res.data.projects   || [];
            this.users      = res.data.user       || res.data.users      || [];
            this.business   = res.data.business   || [];
            this.plans      = res.data.plan       || res.data.plans      || [];
            this.blogs      = res.data.blog       || res.data.blogs      || [];
            this.properties = res.data.property   || res.data.properties || [];
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
    },

    follow(id) {
      axios.post(process.env.VUE_APP_API_URL + 'user/follow', {
        user_id: id,
        followed_by: localStorage.getItem('user'),
      })
        .then(res => { if (res.status === 200) this.fetchData(); })
        .catch(() => {});
    },

    viewTeam(index) {
      this.currentData = this.projects[index];
      this.showTeamModal = true;
    },

    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },
    initAvatar(name, size = 40) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const fs = Math.round(size * 0.35);
      const r = Math.round(size * 0.22);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" rx="${r}" fill="${bg}"/><text x="${size/2}" y="${size*0.64}" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },
  },

  computed: {
    image_url() { return process.env.VUE_APP_IMAGE_URL; },
  },
};
</script>

<style scoped>
/* ── Page ── */
.search-page { padding: 0; }

/* ── Header ── */
.search-header { padding: 2rem 0 1rem; }
.search-header__title {
  font-size: 1.625rem; font-weight: 800; color: var(--text); margin-bottom: .25rem;
}
.search-header__title em { font-style: normal; color: var(--primary); }
.search-header__sub { font-size: .9375rem; color: var(--text-muted); }
.search-filters { display: flex; align-items: center; flex-wrap: wrap; gap: .5rem; margin: .75rem 0 0; }
.search-filter-label { font-size: .8125rem; font-weight: 600; color: var(--text-muted); }
.search-filter-chip {
  display: inline-flex; align-items: center; gap: .25rem;
  padding: .25rem .75rem; background: var(--surface-2, #f1f5f9);
  border-radius: 999px; font-size: .8125rem; color: var(--text); font-weight: 500;
}
.search-filter-clear {
  font-size: .8125rem; color: var(--primary); text-decoration: underline;
  margin-left: .25rem; cursor: pointer;
}

/* ── Filter tabs ── */
.filter-tabs {
  display: flex; gap: .375rem; margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--border); padding-bottom: 0;
}
.filter-tab {
  display: flex; align-items: center; gap: .375rem;
  padding: .625rem 1rem; border: none; background: none;
  font-size: .9rem; font-weight: 600; color: var(--text-muted);
  cursor: pointer; border-bottom: 2px solid transparent;
  margin-bottom: -2px; border-radius: .375rem .375rem 0 0;
  transition: color .15s, border-color .15s, background .15s;
  font-family: inherit;
}
.filter-tab:hover { background: var(--surface-3); color: var(--primary); }
.filter-tab--active { color: var(--primary); border-bottom-color: var(--primary); }
.filter-tab__count {
  font-size: .75rem; font-weight: 700;
  background: var(--surface-3); color: var(--text-muted);
  padding: .1rem .5rem; border-radius: var(--radius-full);
  min-width: 20px; text-align: center;
}
.filter-tab--active .filter-tab__count { background: rgba(30,58,95,.1); color: var(--primary); }

/* ── All-tab sections ── */
.all-section { margin-bottom: 2.5rem; }
.all-section__header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: .625rem;
  border-bottom: 1.5px solid var(--border);
}
.all-section__title {
  display: flex; align-items: center; gap: .5rem;
  font-size: 1.05rem; font-weight: 700; color: var(--text); margin: 0;
}
.all-section__see-all {
  font-size: .875rem; font-weight: 600; color: var(--primary);
  background: none; border: none; cursor: pointer;
  padding: .25rem .5rem; border-radius: var(--radius);
  transition: background .15s; font-family: inherit;
}
.all-section__see-all:hover { background: var(--surface-3); }

/* ── Results grid ── */
.results-grid {
  display: grid; gap: 1.25rem;
}
.results-grid--projects { grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); }
.results-grid--people { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.results-list--people { display: flex; flex-direction: column; gap: 1.25rem; }
.results-grid--companies { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

/* ── Project result card ── */
.result-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: box-shadow .2s, transform .15s;
}
.result-card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); }

.result-card__img-link { display: block; }
.result-card__img {
  width: 100%; height: 180px; object-fit: cover;
  display: block;
}
.result-card__img-placeholder {
  width: 100%; height: 180px;
  background: var(--surface-3);
  display: flex; align-items: center; justify-content: center;
  color: var(--border);
}

.result-card__body { padding: 1rem; flex: 1; display: flex; flex-direction: column; gap: .5rem; }

.result-card__meta-row { display: flex; align-items: center; gap: .5rem; }
.result-card__badge {
  font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  padding: .2rem .6rem; border-radius: var(--radius-full);
}
.result-card__badge--project { background: rgba(30,58,95,.1); color: var(--primary); }
.result-card__badge--property { background: #dcfce7; color: #15803d; }
.result-card__badge--plan { background: #fef3c7; color: #b45309; }
.result-card__status {
  font-size: .75rem; font-weight: 600; padding: .2rem .6rem; border-radius: var(--radius-full);
}
.result-card__status--done { background: rgba(16,185,129,.1); color: var(--success); }
.result-card__status--ongoing { background: rgba(245,158,11,.1); color: #d97706; }

.result-card__title {
  font-size: 1rem; font-weight: 700; color: var(--text);
  text-decoration: none; line-height: 1.35; display: block;
}
.result-card__title:hover { color: var(--primary); text-decoration: none; }

.result-card__location {
  display: flex; align-items: center; gap: .3rem;
  font-size: .8125rem; color: var(--text-muted); margin: 0;
}
.result-card__desc { font-size: .8125rem; color: var(--text-muted); margin: 0; line-height: 1.5; }

.result-card__team {
  display: flex; align-items: center; gap: .625rem;
  cursor: pointer; padding: .5rem; margin: .25rem -.25rem;
  border-radius: .5rem; transition: background .15s;
}
.result-card__team:hover { background: var(--surface-2); }
.team-avatars { display: flex; }
.team-avatar {
  width: 26px; height: 26px; border-radius: 50%;
  object-fit: cover; border: 2px solid var(--surface);
  margin-left: -6px;
}
.team-avatars .team-avatar:first-child { margin-left: 0; }
.team-count { font-size: .8125rem; font-weight: 600; color: var(--text-muted); }

.result-card__cta {
  display: inline-flex; align-items: center; gap: .375rem;
  margin-top: auto; padding-top: .5rem;
  font-size: .8125rem; font-weight: 700; color: var(--primary);
  text-decoration: none; transition: gap .15s;
}
.result-card__cta:hover { gap: .625rem; text-decoration: none; color: var(--accent); }

/* ── Person card ── */
.person-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: box-shadow .2s, transform .15s;
}
.person-card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); }

/* Header row: avatar + info + actions */
.person-card__header {
  display: flex; align-items: center; gap: 1rem;
  padding: 1.25rem 1.25rem .875rem;
}
.person-card__avatar-link { display: block; flex-shrink: 0; }
.person-card__avatar {
  width: 60px; height: 60px; border-radius: 50%;
  object-fit: cover; border: 3px solid var(--surface-3);
  transition: border-color .15s;
}
.person-card__avatar-link:hover .person-card__avatar { border-color: var(--primary); }

.person-card__info { display: block; text-decoration: none; flex: 1; min-width: 0; }
.person-card__name {
  display: block; font-size: .9375rem; font-weight: 700; color: var(--text);
  margin-bottom: .2rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.person-card__info:hover .person-card__name { color: var(--primary); }
.person-card__role {
  display: flex; align-items: center; gap: .25rem;
  font-size: .8125rem; color: var(--text-muted);
}

.person-card__actions { display: flex; gap: .5rem; flex-shrink: 0; }
.person-card__view {
  padding: .4rem .85rem; text-align: center;
  border: 1.5px solid var(--border); border-radius: var(--radius-full);
  font-size: .8125rem; font-weight: 600; color: var(--text);
  text-decoration: none; transition: border-color .15s, color .15s; white-space: nowrap;
}
.person-card__view:hover { border-color: var(--primary); color: var(--primary); text-decoration: none; }

.person-card__follow {
  padding: .4rem .85rem; border-radius: var(--radius-full);
  border: 1.5px solid var(--primary); background: var(--primary);
  font-size: .8125rem; font-weight: 600; color: #fff;
  cursor: pointer; display: flex; align-items: center; gap: .3rem; white-space: nowrap;
  transition: background .15s, border-color .15s;
  font-family: inherit;
}
.person-card__follow:hover { background: #163050; border-color: #163050; }

/* Content sections (projects / properties / plans) */
.person-card__content {
  border-top: 1px solid var(--border);
  padding: .875rem 1.25rem 1rem;
  display: flex; flex-direction: column; gap: .875rem;
}
.person-card__section-label {
  font-size: .75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: var(--text-muted);
  margin: 0 0 .5rem;
}
.person-card__mini-row {
  display: flex; gap: .625rem; overflow-x: auto;
  padding-bottom: .25rem;
  scrollbar-width: thin;
}
.person-card__mini-row::-webkit-scrollbar { height: 4px; }
.person-card__mini-row::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
.person-card__mini-card {
  flex: 0 0 140px;
  display: flex; flex-direction: column; gap: .3rem;
  text-decoration: none;
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--border);
  transition: border-color .15s, box-shadow .15s;
}
.person-card__mini-card:hover { border-color: var(--primary); box-shadow: 0 2px 8px rgba(0,0,0,.08); }
.person-card__mini-img {
  width: 100%; height: 80px; object-fit: cover; display: block;
}
.person-card__mini-placeholder {
  width: 100%; height: 80px; background: var(--surface-3);
  display: flex; align-items: center; justify-content: center; color: var(--text-muted);
}
.person-card__mini-title {
  padding: .3rem .5rem .4rem;
  font-size: .75rem; font-weight: 600; color: var(--text);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  line-height: 1.3;
}

/* ── Company card ── */
.company-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 1rem 1.25rem;
  display: flex; align-items: center; gap: .875rem;
  transition: box-shadow .2s, transform .15s;
}
.company-card:hover { box-shadow: var(--shadow-lg); transform: translateY(-1px); }

.company-card__logo-link { flex-shrink: 0; }
.company-card__logo {
  width: 52px; height: 52px; border-radius: .75rem;
  object-fit: cover; border: 1px solid var(--border);
}
.company-card__info { flex: 1; min-width: 0; }
.company-card__name {
  display: block; font-size: .9375rem; font-weight: 700; color: var(--text);
  text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-bottom: .2rem;
}
.company-card__name:hover { color: var(--primary); text-decoration: none; }
.company-card__loc {
  display: flex; align-items: center; gap: .25rem;
  font-size: .8125rem; color: var(--text-muted);
}
.company-card__cta {
  padding: .4rem 1rem; border-radius: var(--radius-full);
  border: 1.5px solid var(--border); background: none;
  font-size: .8125rem; font-weight: 600; color: var(--text);
  text-decoration: none; flex-shrink: 0;
  transition: border-color .15s, color .15s;
}
.company-card__cta:hover { border-color: var(--primary); color: var(--primary); text-decoration: none; }

/* ── Skeleton ── */
.skeleton-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  overflow: hidden; box-shadow: var(--shadow);
}
.skeleton {
  background: linear-gradient(90deg, var(--surface-3) 25%, var(--surface-2) 50%, var(--surface-3) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: .375rem;
}
.skeleton--img { height: 160px; border-radius: 0; }
.skeleton-card__body { padding: 1rem; display: flex; flex-direction: column; gap: .5rem; }
.skeleton--title { height: 18px; width: 70%; }
.skeleton--text { height: 13px; width: 100%; }
.skeleton--text-short { width: 55%; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Empty state ── */
.search-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 4rem 2rem; text-align: center;
  background: var(--surface); border-radius: var(--radius-lg); box-shadow: var(--shadow);
  color: var(--text-muted);
}
.search-empty svg { margin-bottom: 1.25rem; opacity: .3; }
.search-empty h3 { font-size: 1.125rem; font-weight: 700; color: var(--text); margin-bottom: .375rem; }
.search-empty p { margin-bottom: 1.5rem; max-width: 280px; }

/* ── Modal ── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center;
  padding: 1.5rem;
}
.modal-panel {
  background: var(--surface); border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0,0,0,.18);
  width: 100%; max-width: 560px;
  max-height: calc(100vh - 3rem);
  display: flex; flex-direction: column; overflow: hidden;
}
.modal-panel--sm { max-width: 480px; }
.modal-panel__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.modal-panel__title { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0; }
.modal-panel__close {
  width: 32px; height: 32px; border-radius: 50%; border: none; background: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-muted); transition: background .15s;
}
.modal-panel__close:hover { background: var(--surface-3); color: var(--text); }
.modal-panel__body { padding: 1.25rem 1.5rem; overflow-y: auto; flex: 1; }

/* ── Team member row ── */
.team-member-row {
  display: flex; align-items: center; gap: .75rem;
  padding: .75rem 0; border-bottom: 1px solid var(--border-light);
}
.team-member-row:last-child { border-bottom: none; }
.team-member-row__avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; }
.team-member-row__info { flex: 1; min-width: 0; }
.team-member-row__name {
  display: block; font-size: .9rem; font-weight: 600; color: var(--text);
  text-decoration: none; margin-bottom: .15rem;
}
.team-member-row__name:hover { color: var(--primary); }
.team-member-row__role { font-size: .8125rem; color: var(--text-muted); }

/* ── Modal transition ── */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity .2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

/* ── News grid ── */
.results-grid--news { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
.news-card {
  background: var(--surface); border-radius: var(--radius-lg);
  box-shadow: var(--shadow); overflow: hidden;
  display: flex; flex-direction: column;
  text-decoration: none; color: inherit;
  transition: box-shadow .2s, transform .15s;
}
.news-card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); text-decoration: none; color: inherit; }
.news-card__img-wrap { height: 160px; overflow: hidden; background: var(--surface-3); flex-shrink: 0; }
.news-card__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.news-card__img-placeholder {
  height: 100%; display: flex; align-items: center; justify-content: center; color: var(--border);
}
.news-card__body { padding: 1rem; flex: 1; display: flex; flex-direction: column; gap: .4rem; }
.news-card__cat {
  font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
  color: var(--accent); background: rgba(245,158,11,.1);
  padding: .2rem .6rem; border-radius: var(--radius-full); width: fit-content;
}
.news-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text);
  line-height: 1.35; margin: 0;
}
.news-card:hover .news-card__title { color: var(--primary); }
.news-card__desc { font-size: .8125rem; color: var(--text-muted); line-height: 1.5; margin: 0; flex: 1; }
.news-card__cta {
  display: inline-flex; align-items: center; gap: .3rem; margin-top: .5rem;
  font-size: .8125rem; font-weight: 700; color: var(--primary);
}

/* ── Responsive ── */
/* ── Responsive ── */
@media (max-width: 768px) {
  /* Header */
  .search-header { padding: 1.25rem 0 .75rem; }
  .search-header__title { font-size: 1.2rem; }

  /* Tabs: horizontal scroll so all 6 fit */
  .filter-tabs {
    overflow-x: auto; overflow-y: visible;
    flex-wrap: nowrap;
    gap: .25rem;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0;
  }
  .filter-tabs::-webkit-scrollbar { display: none; }
  .filter-tab {
    flex-shrink: 0;
    padding: .45rem .7rem;
    font-size: .8rem;
    gap: .25rem;
  }
  .filter-tab svg { display: none; }
  .filter-tab__count { padding: .05rem .35rem; }

  /* Grids → single column */
  .results-grid--projects,
  .results-grid--companies { grid-template-columns: 1fr; }

  /* Result cards: smaller image on mobile */
  .result-card__img { height: 160px; }
  .result-card__img-placeholder { height: 160px; }

  /* Person cards */
  .person-card__header { flex-wrap: wrap; gap: .75rem; }
  .person-card__actions { width: 100%; justify-content: stretch; }
  .person-card__view, .person-card__follow { flex: 1; }
  .person-card__mini-card { flex: 0 0 110px; }
  .person-card__mini-img, .person-card__mini-placeholder { height: 62px; }

  /* Company cards */
  .company-card { flex-wrap: wrap; }

  /* Skeleton */
  .skeleton--img { height: 140px; }
}

@media (max-width: 480px) {
  .filter-tab { padding: .4rem .6rem; font-size: .75rem; }
  .result-card__img { height: 140px; }
  .result-card__img-placeholder { height: 140px; }
  .person-card__mini-card { flex: 0 0 95px; }
  .person-card__mini-img, .person-card__mini-placeholder { height: 55px; }
  .search-header__title { font-size: 1.1rem; }
}
</style>
